// Trust copy templates + X-fill expansion + length-based routing.
// Persisted client-side via localStorage.

import { AD_ASSET_STOP_LABELS } from "./adAssetGuards.js";

export const DEFAULT_TEMPLATES = [
    { id: "always-open", text: "Available 24/7 Service", hasX: false },
    { id: "same-day", text: "Same Day Service", hasX: false },
    { id: "licensed", text: "Licensed & Insured", hasX: false },
    { id: "no-hidden", text: "No Hidden Fees at all", hasX: false },
    { id: "free-est", text: "Free Estimates", hasX: false },
    { id: "guarantee", text: "X Months Guarantee", hasX: true, defaultX: "12", xLabel: "Months" },
    { id: "trusted", text: "Trusted by X+ Homeowners", hasX: true, defaultX: "5000", xLabel: "Homeowners" },
    { id: "years-exp", text: "X+ Years of Experience", hasX: true, defaultX: "15", xLabel: "Years" },
    { id: "family", text: "Family-Owned & Operated", hasX: false },
    { id: "five-star", text: "5 star rated by X+ Customers", hasX: true, defaultX: "2000", xLabel: "Customers" },
    { id: "fast-resp", text: "Fast Response Within X Mins", hasX: true, defaultX: "10", xLabel: "Minutes" },
    { id: "book-1min", text: "Book in 1 Minute Online", hasX: false },
];

const STORAGE_KEY = "serpscout_trust_copy_v1";

export const loadTrustCopy = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

export const saveTrustCopy = (state) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
        // localStorage may be unavailable — silently ignore
    }
};

export const defaultTrustState = () => {
    const enabled = {};
    const xValues = {};
    for (const t of DEFAULT_TEMPLATES) {
        enabled[t.id] = true;
        if (t.hasX) xValues[t.id] = t.defaultX;
    }
    return { enabled, xValues, custom: "" };
};

/**
 * Given the user's trust-copy state, return an array of expanded strings.
 * X placeholders are replaced with values, empty X inputs get the default.
 * Templates with `enabled === false` are skipped.
 */
export const expandTrustCopy = (state) => {
    const { enabled = {}, xValues = {}, custom = "" } = state || {};
    const out = [];
    for (const t of DEFAULT_TEMPLATES) {
        if (!enabled[t.id]) continue;
        if (t.hasX) {
            const v = (xValues[t.id] ?? t.defaultX ?? "").trim();
            if (!v) continue;
            out.push(t.text.replace(/X/g, v));
        } else {
            out.push(t.text);
        }
    }
    for (const line of (custom || "").split(/\r?\n/)) {
        const s = line.trim();
        if (s) out.push(s);
    }
    return out;
};

/**
 * Route expanded trust phrases into RSA-fillable buckets by character length.
 *   ≤25   → callout AND headline (short phrases work in both slots)
 *   26-30 → headline only
 *   31-90 → description
 *   >90   → rejected
 * De-duplicates within each bucket (case-insensitive).
 */
export const routeTrustCopy = (expanded) => {
    const headlines = [];
    const descriptions = [];
    const callouts = [];
    const rejected = [];

    const seenH = new Set();
    const seenD = new Set();
    const seenC = new Set();

    // Section-label words that must NEVER show up as an ad asset. Guards against a stale
    // localStorage payload or an accidental paste from a UI label like "Descriptions".
    // Set is imported from a shared module so both this router and the CSV builder can't drift.

    for (const raw of expanded || []) {
        const text = (raw || "").trim();
        if (!text) continue;
        const key = text.toLowerCase();
        const len = text.length;

        if (AD_ASSET_STOP_LABELS.has(key)) {
            rejected.push(text);
            continue;
        }

        if (len > 90) {
            rejected.push(text);
            continue;
        }
        if (len <= 30 && !seenH.has(key)) {
            headlines.push(text);
            seenH.add(key);
        }
        if (len <= 25 && !seenC.has(key)) {
            callouts.push(text);
            seenC.add(key);
        }
        if (len > 30 && len <= 90 && !seenD.has(key)) {
            descriptions.push(text);
            seenD.add(key);
        }
    }
    return { headlines, descriptions, callouts, rejected };
};
